import { Injectable } from '@angular/core';
import { SupabaseService } from './base/supabase.service';
import { KycTraceEvent } from './kyc-trace.service';

/**
 * Reads the RECORDED trace of a KYC attempt back from the database.
 *
 * The live {@link KycTraceService} streams a KYC attempt to `cp_event` as append-only
 * `KYC_ATTEMPT_TRACE` batches (event type 62). Each batch row carries its correlation keys
 * inside `context`:
 *   { attempt_id, kyc_check_id, attempt_label, batch_from_seq, batch_to_seq, events: KycTraceEvent[] }
 *
 * To reconstruct a check's full timeline we:
 *   1. find every `attempt_id` whose batches were tagged with this `kyc_check_id`, then
 *   2. gather ALL batches for those attempts (including the early ones logged before the
 *      check existed, whose `kyc_check_id` is still null), and
 *   3. flatten + de-duplicate + order their `events`.
 *
 * Both lookups hit the partial expression indexes seeded for type 62
 * (`cp_event_kyc_trace_check_idx`, `cp_event_kyc_trace_attempt_idx`).
 *
 * Read-only. Row visibility is governed by RLS — the same policy that lets an agent read a
 * client's `cp_kyc_check` covers the client-authored trace events.
 */

/** The KYC attempt-trace analytics event type (see cp_event_type seed migration). */
const KYC_ATTEMPT_TRACE_TYPE = 62;

export interface RecordedTrace {
  events: KycTraceEvent[];
  /** Human label of the attempt, if any batch carried one. */
  attemptLabel: string | null;
  /** Distinct attempt ids that fed this timeline (usually one). */
  attemptIds: string[];
  /** Server-stamped audit: source IP(s) the batches arrived from, and the receive-time span. */
  serverAudit: ServerAudit | null;
}

/** Server-observed audit stamped by the event-push lambda (type 62 only). */
export interface ServerAudit {
  sourceIps: string[];
  firstReceivedAt: string | null;
  lastReceivedAt: string | null;
}

/** The subset of a batch row's `context` we rely on. */
interface TraceContext {
  attempt_id?: string | null;
  kyc_check_id?: string | null;
  attempt_label?: string | null;
  events?: KycTraceEvent[] | null;
  /** Server audit stamped at ingest: { source_ip, received_at }. */
  server?: { source_ip?: string | null; received_at?: string | null } | null;
}

@Injectable({ providedIn: 'root' })
export class KycTraceReadService {
  constructor(private sbs: SupabaseService) {}

  /** Full recorded timeline for one `cp_kyc_check`, newest-attempt-inclusive, ordered chronologically. */
  async forCheck(kycCheckId: string): Promise<RecordedTrace> {
    if (!kycCheckId) return empty();

    // 1. Which attempt(s) produced this check?
    const tagged = await this.rows('context->>kyc_check_id', 'eq', kycCheckId);
    const attemptIds = uniq(
      tagged.map((c) => c.attempt_id).filter((v): v is string => !!v),
    );

    // 2. Prefer the by-attempt superset (captures pre-check batches). Fall back to the
    //    tagged rows alone if no attempt_id was recorded.
    const batches = attemptIds.length
      ? await this.rows('context->>attempt_id', 'in', attemptIds)
      : tagged;

    return this.assemble(batches, attemptIds);
  }

  /** Full recorded timeline for a single attempt id (correlates pre-check batches too). */
  async forAttempt(attemptId: string): Promise<RecordedTrace> {
    if (!attemptId) return empty();
    const batches = await this.rows('context->>attempt_id', 'eq', attemptId);
    return this.assemble(batches, [attemptId]);
  }

  // ── internals ────────────────────────────────────────────────────────────────
  private async rows(
    column: string,
    op: 'eq' | 'in',
    value: string | string[],
  ): Promise<TraceContext[]> {
    let q = this.sbs.supabase
      .from('cp_event')
      .select('context')
      .eq('type', KYC_ATTEMPT_TRACE_TYPE)
      .order('created_at', { ascending: true })
      .limit(1000);

    q = op === 'in' ? q.in(column, value as string[]) : q.eq(column, value as string);

    const { data, error } = await q;
    if (error) throw new Error(error.message);
    return ((data as Array<{ context: TraceContext | null }>) ?? [])
      .map((r) => r.context)
      .filter((c): c is TraceContext => !!c);
  }

  private assemble(batches: TraceContext[], attemptIds: string[]): RecordedTrace {
    const seen = new Set<string>();
    const events: KycTraceEvent[] = [];
    let attemptLabel: string | null = null;

    // Server audit stamped per batch by the event-push lambda (source IP + receive time).
    const sourceIps = new Set<string>();
    const receivedAts: string[] = [];

    for (const ctx of batches) {
      if (!attemptLabel && ctx.attempt_label) attemptLabel = ctx.attempt_label;
      if (ctx.server?.source_ip) sourceIps.add(ctx.server.source_ip);
      if (ctx.server?.received_at) receivedAts.push(ctx.server.received_at);
      const list = Array.isArray(ctx.events) ? ctx.events : [];
      for (const e of list) {
        // seq resets per attempt, so key by attempt to keep retries distinct.
        const key = `${ctx.attempt_id ?? '?'}:${e.seq}`;
        if (seen.has(key)) continue;
        seen.add(key);
        events.push(e);
      }
    }

    // Chronological: wall-clock first (correct across attempts), seq as the tie-break.
    events.sort((a, b) => a.t - b.t || a.seq - b.seq);

    // ISO strings sort lexicographically = chronologically.
    receivedAts.sort();
    const serverAudit: ServerAudit | null =
      sourceIps.size || receivedAts.length
        ? {
            sourceIps: [...sourceIps],
            firstReceivedAt: receivedAts[0] ?? null,
            lastReceivedAt: receivedAts[receivedAts.length - 1] ?? null,
          }
        : null;

    return {
      events,
      attemptLabel,
      attemptIds: attemptIds.length
        ? attemptIds
        : uniq(batches.map((c) => c.attempt_id).filter((v): v is string => !!v)),
      serverAudit,
    };
  }
}

function empty(): RecordedTrace {
  return { events: [], attemptLabel: null, attemptIds: [], serverAudit: null };
}

function uniq<T>(xs: T[]): T[] {
  return [...new Set(xs)];
}
